import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc as setFirebaseDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getStreaks, getLeaderboard } from '../utils/streakUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import html2canvas from 'html2canvas';
import '../styles/HomePage.css';

// Components
import HeroSection from '../components/home/HeroSection';
import StatsBar from '../components/home/StatsBar';
import FeaturesShowcase from '../components/home/FeaturesShowcase';
import InteractiveDemo from '../components/home/InteractiveDemo';
import Testimonials from '../components/home/Testimonials';
import CTASection from '../components/home/CTASection';
import CoachingPromoSection from '../components/home/CoachingPromoSection';
import DashboardBento from '../components/home/DashboardBento';
import LeaderboardCard from '../components/home/LeaderboardCard';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import AuthPage from './AuthPage';

function HomePage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const isLoggedIn = !!userId;

  const [username, setUsername] = useState('');
  const [examType, setExamType] = useState('TYT–AYT');
  const [target, setTarget] = useState('');
  const [daysLeft, setDaysLeft] = useState(0);
  const [examDate, setExamDate] = useState(null);
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [streakBadges, setStreakBadges] = useState([]);
  const [dailyQuote, setDailyQuote] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRankData, setUserRankData] = useState(null);

  // Modal states
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareData, setShareData] = useState({ tasks: '', hours: '' });

  const shareCardRef = useRef(null);

  const quotes = [
    'Hedefine ulaşmak için her gün bir adım at, küçük adımlar büyük başarılar getirir! 🚢',
    'Başarı, pes etmeyenlerin ödülüdür. Devam et, kaptan! 🌟',
    'Rüzgarı arkana al, yelkenlerini aç ve hedeflerine doğru ilerle! ⛵',
    'Her zorluk, seni daha güçlü bir kaptan yapar. Asla vazgeçme! 💪',
    'Bugün yaptığın çalışma, yarınki başarının temelidir. Devam et! 📚',
  ];

  // Auth listener
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => setUserId(user?.uid || null));
    return () => unsub();
  }, []);

  // Daily quote
  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    let hash = 0;
    for (let i = 0; i < todayStr.length; i++) {
      hash = todayStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % quotes.length;
    setDailyQuote(quotes[index]);
  }, []);

  // User data
  useEffect(() => {
    if (!isLoggedIn) return;
    (async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const d = snap.data();
          setUsername(d.username || '');
          setExamType(d.examType || 'TYT–AYT');
          setTarget(d.target || '');
          setXp(d.xp || 0);
          setLevel(d.level || 1);
          setStreakBadges(d.streakBadges || []);
          if (d.examDate) { setExamDate(d.examDate); calculateDaysLeft(d.examDate); }
        }
        const streakData = await getStreaks(user.uid);
        setStreak(streakData.currentStreak || 0);
        const lb = await getLeaderboard(examType, 'weekly', user.uid);
        setLeaderboard(lb.top10 || []);
        setUserRankData(lb.userRankData || null);
      } catch (err) { console.error('HomePage data error:', err.message); }
    })();
  }, [isLoggedIn, examType]);

  useEffect(() => { if (examDate) calculateDaysLeft(examDate); }, [examDate]);

  const calculateDaysLeft = (date) => {
    const diff = new Date(date) - new Date();
    setDaysLeft(Math.max(0, Math.ceil(diff / 86400000)));
  };

  const handleSetExamDate = async (e) => {
    e.preventDefault();
    const d = e.target.examDate.value;
    if (!d) { toast.error('Lütfen bir tarih seçin.'); return; }
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Oturum bulunamadı.');
      await setFirebaseDoc(doc(db, 'users', uid), { examDate: d }, { merge: true });
      setExamDate(d); calculateDaysLeft(d); setShowDateModal(false);
      toast.success('Sınav tarihi kaydedildi!');
    } catch (err) { toast.error(`Tarih kaydedilirken hata: ${err.message}`); }
  };

  const handleSetTarget = async (e) => {
    e.preventDefault();
    const t = e.target.targetName.value;
    if (!t) { toast.error('Lütfen bir hedef girin.'); return; }
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Oturum bulunamadı.');
      await setFirebaseDoc(doc(db, 'users', uid), { target: t }, { merge: true });
      setTarget(t); setShowTargetModal(false);
      toast.success('Hedef kaydedildi! Başarılar! 🚀');
    } catch (err) { toast.error(`Hedef kaydedilirken hata: ${err.message}`); }
  };

  const handleShareSuccess = async () => {
    if (!shareData.tasks || !shareData.hours) { toast.error('Lütfen tüm alanları doldurun.'); return; }
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Oturum bulunamadı.');
      await setFirebaseDoc(doc(db, 'users', uid), { xp: xp + 10 }, { merge: true });
      setXp(xp + 10);
      if (shareCardRef.current) {
        const canvas = await html2canvas(shareCardRef.current, { scale: 2 });
        const link = document.createElement('a');
        link.download = 'basari-karti.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
      setShowShareCard(false); setShareData({ tasks: '', hours: '' });
      toast.success('Başarı kartı oluşturuldu ve 10 XP kazandın! 🎉');
    } catch (err) { toast.error(`Hata: ${err.message}`); }
  };

  return (
    <div className="home-page">
      {isLoggedIn ? (
        <div className="home-logged-in">
          <DashboardBento
            userId={userId} username={username} examType={examType} target={target}
            daysLeft={daysLeft} examDate={examDate} streak={streak}
            xp={xp} level={level} dailyQuote={dailyQuote}
            onSetDate={() => setShowDateModal(true)}
            onSetTarget={() => setShowTargetModal(true)}
            onShareCard={() => setShowShareCard(true)}
          />
          <LeaderboardCard leaderboard={leaderboard} userId={userId} userRankData={userRankData} />
        </div>
      ) : (
        <div className="home-guest">
          <HeroSection onAuthOpen={() => setAuthModalOpen(true)} />
          <StatsBar />
          <FeaturesShowcase />
          <InteractiveDemo onAuthOpen={() => setAuthModalOpen(true)} />
          <CoachingPromoSection />
          <Testimonials />
          <CTASection onAuthOpen={() => setAuthModalOpen(true)} />
        </div>
      )}

      {/* Auth Modal */}
      <Modal isOpen={authModalOpen && !isLoggedIn} onClose={() => setAuthModalOpen(false)} title="Giriş Yap / Kayıt Ol" maxWidth="420px">
        <AuthPage onClose={() => setAuthModalOpen(false)} />
      </Modal>

      {/* Exam Date Modal */}
      <Modal isOpen={showDateModal} onClose={() => setShowDateModal(false)} title="Sınav Tarihini Belirle">
        <form onSubmit={handleSetExamDate} className="flex flex-col gap-4">
          <input type="date" name="examDate" defaultValue={examDate || ''} required className="input input-lg" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: '12px' }} />
          <div className="flex gap-3 justify-between mt-2">
            <Button variant="ghost" onClick={() => setShowDateModal(false)}>İptal</Button>
            <Button variant="primary" type="submit">Kaydet</Button>
          </div>
        </form>
      </Modal>

      {/* Target Modal */}
      <Modal isOpen={showTargetModal} onClose={() => setShowTargetModal(false)} title="Hedefini Belirle">
        <form onSubmit={handleSetTarget} className="flex flex-col gap-4">
          <input type="text" name="targetName" defaultValue={target} placeholder="Örn: Boğaziçi Bilgisayar" required className="input input-lg" />
          <div className="flex gap-3 justify-between">
            <Button variant="ghost" onClick={() => setShowTargetModal(false)}>İptal</Button>
            <Button variant="primary" type="submit">Kaydet</Button>
          </div>
        </form>
      </Modal>

      {/* Share Card Modal */}
      <Modal isOpen={showShareCard} onClose={() => setShowShareCard(false)} title="Başarı Kartı Oluştur" maxWidth="450px">
        <div className="flex flex-col gap-4">
          <input type="number" className="input" placeholder="Tamamlanan görev sayısı" name="tasks" value={shareData.tasks} onChange={(e) => setShareData({ ...shareData, tasks: e.target.value })} />
          <input type="number" className="input" placeholder="Çalışma süresi (saat)" name="hours" value={shareData.hours} onChange={(e) => setShareData({ ...shareData, hours: e.target.value })} />

          <div
            className="share-preview"
            ref={shareCardRef}
            style={{
              position: 'relative',
              background: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '24px',
              padding: '32px 24px',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.1)',
              overflow: 'hidden',
              margin: '20px 0'
            }}
          >
            {/* Glow / Neon effects */}
            <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: 'rgba(16, 185, 129, 0.4)', filter: 'blur(60px)', zIndex: 0 }}></div>
            <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '150px', height: '150px', background: 'rgba(99, 102, 241, 0.4)', filter: 'blur(60px)', zIndex: 0 }}></div>

            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', marginBottom: '16px', boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)', border: '2px solid rgba(255,255,255,0.2)' }}>
                🚀
              </div>

              <h4 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', marginBottom: '4px', letterSpacing: '-0.02em' }}>
                {username}
              </h4>
              <p style={{ color: 'var(--accent-primary)', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '24px' }}>
                Yelken Kaptanı
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', width: '100%', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '16px', backdropFilter: 'blur(10px)' }}>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{shareData.tasks || '0'}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', textTransform: 'uppercase', fontWeight: 600 }}>Görev</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '16px', backdropFilter: 'blur(10px)' }}>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{shareData.hours || '0'}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', textTransform: 'uppercase', fontWeight: 600 }}>Saat</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', width: '100%', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-warning)' }}>{streak}🔥</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', marginTop: '4px' }}>Streak</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#6366f1' }}>{level}⭐</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', marginTop: '4px' }}>Seviye</div>
                </div>
              </div>

              <div style={{ marginTop: '24px', opacity: 0.5 }}>
                <svg width="60" height="20" viewBox="0 0 100 30">
                  <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold" letterSpacing="2">YELKEN</text>
                </svg>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-between">
            <Button variant="ghost" onClick={() => setShowShareCard(false)}>Kapat</Button>
            <Button variant="primary" onClick={handleShareSuccess}>İndir ve Paylaş</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default HomePage;